import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import useUsers from '../../../../../hooks/users/useUsers';
import DataGrid from '../../../../table/DataGrid';
import IconTooltipButton from '../../../../button/IconTooltipButton';
import FormatDate from '../../../../date/FormatDate';
import TextLog from '../../../../text/TextLog';
import Module from '../../../../layout/Module';
import Chip from '@mui/material/Chip';
import { updateUsersPreview } from '../../../../../store/actions/user.actions';
import { useNavigate } from 'react-router-dom';
import FilterButton from '../Filter/Button';
import DialogAction from '../Dialog/Dialog';
import { Checkbox, Typography } from '@mui/material';
import Avatar from '../../../../avatar/Avatar';
import MenuButton from '../../../../button/MenuButton';

const UsersList = () => {
  const navigate = useNavigate();
  const domains = useSelector(
    (state) => state?.tenant?.sso?.filter((sso) => sso.isEnabled).map((sso) => sso.domain) || []
  );

  const permissions = useSelector((state) => state?.session?.permissions?.user || {});
  const userIdSession = useSelector((state) => state?.session?.user?.userId);

  const list = useSelector((state) => state?.user?.list || []);
  const totalList = useSelector((state) => state?.user?.totalList || []);
  const dispatch = useDispatch();
  const { isLoading, isLoadingList, getUsers } = useUsers();

  const state = useSelector((state) => state?.filter?.user || {});
  const skip = useSelector((state) => state?.datatable?.user?.skip || 0);
  const rowsPerPage = useSelector((state) => state?.datatable?.user?.rowsPerPage || 100);
  const columnsTable = useSelector((state) => state?.datatable?.user?.columnsTable || []);

  const [modal, setModal] = useState(null);
  const [checkIds, setCheckIds] = useState([]);

  const onSelectedAll = () => {
    if (checkIds.length === 0) {
      setCheckIds(totalList.map((row) => row._id));
    } else {
      setCheckIds([]);
    }
  };
  const onSelected = (id, checked) => {
    if (checked) {
      setCheckIds([...checkIds, id]);
    } else {
      setCheckIds(checkIds.filter((item) => item !== id));
    }
  };

  useEffect(() => {
    getUsers(skip, rowsPerPage, state.filter);
  }, [rowsPerPage, skip, state?.filter]);

  const options = [
    {
      label: 'Send invitation',
      permissions: permissions?.['batch-actions']?.includes('send-invite'),
      onClick: () => setModal({ type: 'bulk send invitation', data: { _id: checkIds } })
    },
    {
      label: 'Recovery password',
      permissions: permissions?.['batch-actions']?.includes('recovery-passwordd'),
      onClick: () => setModal({ type: 'bulk recovery password', data: { _id: checkIds } })
    },
    {
      divider: true,
      label: 'Update roles',
      permissions: permissions?.['batch-actions']?.includes('edit-roles'),
      onClick: () => setModal({ type: 'batch update roles', data: { _id: checkIds } })
    }
  ];

  const onReset = () => {
    setCheckIds([]);
  };

  return (
    <>
      <Module.BodyHeader className='border-b border-b-2 border-primary justify-between'>
        <FilterButton />
        {checkIds.length > 1 && (
          <MenuButton title='Actions' icon='ellipsis-v' color='primary' items={options} />
        )}
      </Module.BodyHeader>
      <Module.BodyContainer>
        <DataGrid
          id='users'
          rows={list.map((row, index) => ({
            ...row,
            index: index + 1 + rowsPerPage * skip,
            firstName: row?.preferredName || row?.firstName,
            avatar: <Avatar size='small' variant='circular' alt={row?.code} src={`photos/${row?.photo}`} />,
            roles: (
              <div className='flex flex-wrap gap-1'>
                {row?.roles?.map((role) => (
                  <Chip key={`user-${row._id}-role-${role._id}`} size='small' label={role.name} />
                ))}
              </div>
            ),
            lastLogin: row?.lastLogin ? (
              <Chip size='small' label={<FormatDate date={row.lastLogin} />} variant='outlined' />
            ) : (
              ''
            ),
            yearLevelUser: (
              <div className='flex flex-wrap items-center gap-1'>
                {row?.yearLevel?.map((yearLevel) => (
                  <Chip
                    key={`user-${row._id}-yearLevel-${yearLevel._id}`}
                    size='small'
                    label={yearLevel.name}
                    variant='outlined'
                  />
                ))}
              </div>
            ),
            flag: (
              <div className='flex flex-wrap items-center gap-1'>
                {row?.data?.flag?.map((flag) => (
                  <Chip key={`user-${row._id}-flag-${flag}`} size='small' label={flag} variant='outlined' />
                ))}
              </div>
            ),
            activated: (
              <Chip
                size='small'
                variant='outlined'
                style={{
                  color: row.activated ? 'var(--accent)' : 'var(--secondary)',
                  border: row.activated ? '1px solid var(--accent)' : '1px solid var(--secondary)'
                }}
                label={row.activated ? 'Yes' : 'No'}
              />
            ),
            log: <TextLog updatedAt={row.updatedAt} updatedBy={row.updatedBy} />,
            lastInvited: row?.lastInvited ? (
              <Chip size='small' label={<FormatDate date={row.lastInvited} />} />
            ) : (
              ''
            ),

            checkIds: (
              <Checkbox
                id={`checkbox-id-user-${row._id}`}
                checked={checkIds.includes(row._id)}
                onChange={(e) => onSelected(row._id, e.target.checked)}
                color='primary'
              />
            ),
            actions: (
              <div className='flex items-center gap-1 items-center justify-center'>
                {permissions?.preview && (
                  <IconTooltipButton
                    id={`preview-${row._id}`}
                    disabled={isLoading}
                    title='Preview'
                    icon='expand-alt'
                    onClick={() => dispatch(updateUsersPreview(row._id))}
                  />
                )}
                {permissions?.updated && (
                  <IconTooltipButton
                    id={`edit-${row._id}`}
                    disabled={isLoading}
                    title='Edit'
                    icon='pen'
                    onClick={() => navigate(`/dashboard/users/edit/${row._id}`)}
                  />
                )}
                {domains.includes(row?.email.split('@')[1]) &&
                !row.roles.some((role) => role.name === 'Parent') ? (
                  <div className='px-4' />
                ) : row?.activated ? (
                  permissions['recovery-password'] && (
                    <IconTooltipButton
                      id={`recovery-password-${row._id}`}
                      disabled={isLoading}
                      title='Recovery password'
                      icon='key'
                      style={{
                        color: 'var(--muted-foreground)'
                      }}
                      onClick={() => setModal({ type: 'recovery password', data: row })}
                    />
                  )
                ) : (
                  permissions['send-invite'] && (
                    <IconTooltipButton
                      id={`send-invite-${row._id}`}
                      title='Send invitation'
                      icon='paper-plane'
                      style={{
                        color: row?.invited === 0 ? 'var(--secondary)' : 'var(--accent)'
                      }}
                      onClick={() => setModal({ type: 'send invitation', data: row })}
                    />
                  )
                )}
                {permissions.archived && (
                  <IconTooltipButton
                    id={`archive-${row._id}`}
                    disabled={isLoading}
                    title='Archive'
                    icon='archive'
                    onClick={() => setModal({ type: 'archive', data: row })}
                  />
                )}
                {permissions.deleted && (
                  <IconTooltipButton
                    id={`delete-${row._id}`}
                    disabled={isLoading}
                    title='Delete'
                    icon='trash-alt'
                    onClick={() => setModal({ type: 'delete', data: row })}
                  />
                )}
                {permissions['login-impersonate'] && String(row._id) !== String(userIdSession) ? (
                  <IconTooltipButton
                    title='Login impersonate'
                    icon='sign-in'
                    color='primary'
                    onClick={() => setModal({ type: 'login impersonate', data: row })}
                  />
                ) : (
                  <div className='px-4' />
                )}
              </div>
            )
          }))}
          loading={isLoadingList}
          totalRows={totalList.length}
          skip={skip}
          rowsPerPage={rowsPerPage}
          actionsSetting={
            checkIds.length > 0 && (
              <Typography className='min-w-100px' align='right'>
                Rows selected {checkIds.length}
              </Typography>
            )
          }
          columns={[
            { label: '#', key: 'index', align: 'center', width: 10, show: true, hide: false },
            {
              show: permissions['batch-actions'],
              key: 'checkIds',
              align: 'center',
              width: 15,
              label: (
                <Checkbox
                  id={`checkbox-id-user-header`}
                  checked={checkIds.length > 0 && checkIds.length === totalList.length}
                  indeterminate={checkIds.length > 0 && checkIds.length < totalList.length}
                  onChange={(e) => onSelectedAll(e.target.checked)}
                  color='primary'
                />
              )
            },
            ...columnsTable,
            {
              label: 'Log',
              key: 'log',
              align: 'center',
              className: 'text-center box-center',
              width: 120,
              show: true,
              hide: false
            },
            { label: 'Actions', key: 'actions', align: 'center', width: 50, show: true, hide: false }
          ]}
          nameModule='user'
          labelNoRows="You don't have any users yet"
        />
      </Module.BodyContainer>
      {modal && (
        <DialogAction
          show={!!modal}
          onReset={onReset}
          onClose={() => setModal(null)}
          type={modal.type}
          form={modal.data}
        />
      )}
    </>
  );
};

export default UsersList;
